<template>
  <component
    :is="getElement(media?.Link)"
    v-if="media?.Media?.url && media?.Media?.mime?.includes('image')"
    class="media"
    :to="media.Link"
  >
    <picture class="media__picture">
      <source
        :srcset="widescreenImage"
        media="(min-width: 1280px)"
      >
      <source
        :srcset="desktopImage"
        media="(min-width: 1024px)"
      >
      <source
        :srcset="tabletImage"
        media="(min-width: 768px)"
      >
      <source
        :srcset="phabletImage"
        media="(min-width: 640px)"
      >
      <source
        :srcset="mobileImage"
      >
      <img
        class="media__image"
        :src="widescreenImage"
        :alt="`${media.Media?.alternativeText || media.Media?.caption || ''}`"
        :width="media.Media.width"
        :height="media.Media.height"
        :loading="lazy ? 'lazy' : 'eager'"
        :fetchpriority="lazy ? 'low' : 'high'"
        :decoding="lazy ? 'async' : 'sync'"
      >
    </picture>
  </component>

  <div
    v-else-if="media?.Media?.url && media?.Media?.mime?.includes('video')"
    class="media__video"
  >
    <UiVideo
      v-if="media?.Media?.url"
      :class="{ 'mq--desktop-plus': media?.Media?.url && media?.MediaMobile?.url }"
      :src="media.Media?.url"
      :alt="`${media.Media?.alternativeText || media.Media?.caption || ''}`"
      :width="media.Media.width"
      :height="media.Media.height"
      :loading="lazy ? 'lazy' : 'eager'"
      :fetchpriority="lazy ? 'low' : 'high'"
      :controls="false"
      autoplay
      muted
      loop
    />
    <UiVideo
      v-if="media?.MediaMobile?.url"
      class="mq--mobile-phablet"
      :src="media.MediaMobile?.url"
      :alt="`${media.MediaMobile?.alternativeText || media.MediaMobile?.caption || ''}`"
      :width="media.MediaMobile.width"
      :height="media.MediaMobile.height"
      :loading="lazy ? 'lazy' : 'eager'"
      :fetchpriority="lazy ? 'auto' : 'high'"
      :controls="false"
      autoplay
      muted
      loop
    />

    <!-- <pre>{{ media }}</pre> -->
  </div>
</template>

<script lang="ts" setup>
import { AppLink } from '#components'

const props = withDefaults(defineProps<{
  media: any
  lazy: boolean
  bannerCount?: number
  fullWidth?: boolean
}>(), {
  bannerCount: 1,
  fullWidth: false,
})

function getElement(to: any) {
  return to ? AppLink : 'div'
}

const widescreenImage = computed(() => {
  const columns = Math.min(props?.bannerCount, 3)
  const containerWidth = props.fullWidth ? 2560 : 1440
  const baseWidth = Math.round(containerWidth / columns)
  const retinaWidth = baseWidth * 2 // Account for retina displays
  const maxWidth = Math.min(retinaWidth, props?.media.Media?.width || retinaWidth)
  return props?.media.Media?.url?.replaceAll(' ', '%20') + '?f=rs:fit:' + maxWidth
})

const desktopImage = computed(() => {
  const columns = Math.min(props?.bannerCount, 3)
  const baseWidth = Math.round(1280 / columns)
  const retinaWidth = baseWidth * 2
  const maxWidth = Math.min(retinaWidth, props?.media.Media?.width || retinaWidth)
  return props?.media.Media?.url?.replaceAll(' ', '%20') + '?f=rs:fit:' + maxWidth
})

const tabletImage = computed(() => {
  const columns = Math.min(props?.bannerCount, 2)
  const baseWidth = Math.round(1024 / columns)
  const retinaWidth = baseWidth * 2
  const maxWidth = Math.min(retinaWidth, props?.media.Media?.width || retinaWidth)
  return props?.media.Media?.url?.replaceAll(' ', '%20') + '?f=rs:fit:' + maxWidth
})

const phabletImage = computed(() => {
  const baseWidth = 768
  const retinaWidth = baseWidth * 2
  const originalWidth = props?.media.MediaMobile?.width || props?.media.Media?.width
  const maxWidth = Math.min(retinaWidth, originalWidth || retinaWidth)
  const url = (props?.media.MediaMobile?.url || props?.media.Media?.url)?.replaceAll(' ', '%20')
  return url + '?f=rs:fit:' + maxWidth
})

const mobileImage = computed(() => {
  const baseWidth = 640
  const retinaWidth = baseWidth * 2
  const originalWidth = props?.media.MediaMobile?.width || props?.media.Media?.width
  const maxWidth = Math.min(retinaWidth, originalWidth || retinaWidth)
  const url = (props?.media.MediaMobile?.url || props?.media.Media?.url)?.replaceAll(' ', '%20')
  return url + '?f=rs:fit:' + maxWidth
})
</script>

<style>
@import url("~layers/app/components/Strapi/Media.css");
</style>
